import React, { type ReactElement } from "react";
import { ChevronLeft, ChevronRight, Info, Repeat2, X } from "lucide-react";
import { Button, cn } from "@ndd/ui";
import {
  BLOCK_TONE_CLASS,
  BREAK_HATCH,
  type BlockTone,
} from "../../lib/rd-schedule-week.js";

// NO-874: the Week view's header row (the week with arrows and Today, the
// "Show missed, cancelled and rescheduled" checkbox, the Key toggle) and the
// Key box that opens under it. Presentational, relative imports only.

export const WEEK_KEY_ID = "week-calendar-key";

export interface WeekToolbarProps {
  title: string;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  showMissed: boolean;
  onShowMissedChange: (checked: boolean) => void;
  keyOpen: boolean;
  onKeyOpenChange: (open: boolean) => void;
}

export function WeekToolbar(props: WeekToolbarProps): ReactElement {
  return (
    <div className="mb-3.5 mt-[18px] flex flex-wrap items-center gap-2">
      <h2 className="mr-2 font-sans text-title font-bold leading-title text-foreground">
        {props.title}
      </h2>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-8 rounded-md"
        aria-label="Previous week"
        onClick={props.onPrevious}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-8 rounded-md"
        aria-label="Next week"
        onClick={props.onNext}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="utility"
        size="compact"
        className="h-8 rounded-md px-3 text-body"
        onClick={props.onToday}
      >
        Today
      </Button>
      <span className="grow" />
      <label className="flex cursor-pointer items-center gap-2 text-body-sm text-muted-strong">
        <input
          type="checkbox"
          checked={props.showMissed}
          onChange={(event) => props.onShowMissedChange(event.target.checked)}
          className="size-4 rounded border-border-strong accent-primary"
        />
        Show missed, cancelled and rescheduled
      </label>
      <Button
        type="button"
        variant="utility"
        size="compact"
        className="h-8 rounded-md px-3 text-body aria-expanded:bg-primary-soft"
        aria-expanded={props.keyOpen}
        aria-controls={WEEK_KEY_ID}
        onClick={() => props.onKeyOpenChange(!props.keyOpen)}
      >
        <Info aria-hidden="true" className="size-[15px]" />
        Key
      </Button>
    </div>
  );
}

interface KeyItem {
  name: string;
  tone: BlockTone | "gcal";
}

const WHAT_HAPPENED: readonly KeyItem[] = [
  { name: "Upcoming", tone: "upcoming" },
  { name: "Done", tone: "done" },
  { name: "Cancelled", tone: "cancelled" },
  { name: "Rescheduled", tone: "rescheduled" },
  { name: "Late cancel", tone: "late_cancel" },
  { name: "No-show", tone: "no_show" },
];

// Google Calendar events are designed (dotted blocks) but not fed to the Week
// yet, so the Key leaves them out. Revisit when Google Calendar sync lands:
// turn this on and draw the events on the grid.
const SHOW_GOOGLE_CALENDAR = false;

const YOUR_TIME: readonly KeyItem[] = [
  { name: "Break or personal", tone: "break" },
  { name: "Project", tone: "project" },
  ...(SHOW_GOOGLE_CALENDAR ? [{ name: "Google Calendar", tone: "gcal" } as const] : []),
];

const SWATCH_BASE = "size-3 shrink-0 rounded-[4px]";

function swatchClass(tone: KeyItem["tone"]): string {
  if (tone === "gcal") return "border border-dotted border-border-strong bg-background";

  return BLOCK_TONE_CLASS[tone];
}

/** The key box: what happened, your time, and the marks. Closes on its own button. */
export function WeekKey({ onClose }: { onClose: () => void }): ReactElement {
  return (
    <section
      id={WEEK_KEY_ID}
      aria-label="Calendar key"
      className="relative mb-3.5 flex flex-wrap gap-x-10 gap-y-3.5 rounded-lg border border-border bg-card py-4 pl-5 pr-[52px] text-body-sm text-foreground"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="absolute right-2.5 top-2.5 size-[30px] rounded-md text-muted-strong"
        aria-label="Hide key"
        onClick={onClose}
      >
        <X aria-hidden="true" />
      </Button>
      <KeyGroup title="What happened" items={WHAT_HAPPENED} />
      <KeyGroup title="Your time" items={YOUR_TIME} />
      <div className="flex min-w-0 flex-col gap-2">
        <span className="text-label text-muted-foreground">Marks</span>
        <ul className="m-0 grid list-none grid-cols-[repeat(2,minmax(0,auto))] gap-x-[22px] gap-y-1.5 p-0">
          <MarkItem label="New patient">
            <MarkTag text="INITIAL" />
          </MarkItem>
          <MarkItem label="Recurring">
            <span
              aria-hidden="true"
              className="grid size-4 place-items-center rounded-full bg-primary text-primary-foreground"
            >
              <Repeat2 className="size-2.5" strokeWidth={3} />
            </span>
          </MarkItem>
          <MarkItem label="S code visit">
            <MarkTag text="S CODE" />
          </MarkItem>
          <MarkItem label="No next visit booked">
            <span
              aria-hidden="true"
              className="grid size-4 place-items-center rounded-full bg-destructive font-sans text-micro font-extrabold leading-none text-destructive-foreground"
            >
              !!
            </span>
          </MarkItem>
        </ul>
      </div>
    </section>
  );
}

function KeyGroup({
  title,
  items,
}: {
  title: string;
  items: readonly KeyItem[];
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-label text-muted-foreground">{title}</span>
      <ul className="m-0 grid list-none grid-cols-[repeat(2,minmax(0,auto))] gap-x-[22px] gap-y-1.5 p-0">
        {items.map((item) => (
          <li key={item.name} className="flex items-center gap-2 whitespace-nowrap">
            <span
              aria-hidden="true"
              className={cn(SWATCH_BASE, swatchClass(item.tone))}
              style={
                item.tone === "break" ? { backgroundImage: BREAK_HATCH } : undefined
              }
            />
            {item.name}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MarkItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <li className="flex items-center gap-2 whitespace-nowrap">
      {children}
      {label}
    </li>
  );
}

function MarkTag({ text }: { text: string }): ReactElement {
  return (
    <span className="rounded-[4px] border border-foreground px-1 font-mono text-eyebrow uppercase leading-[15px] tracking-[0.06em]">
      {text}
    </span>
  );
}
